import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import LoadingSpinner from '@/components/LoadingSpinner';
import { apiClient } from '@/api/client';
import { formatDateTime } from '@/utils/formatters';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.amebogpt.com.ng';

const APIKeysPage = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testApiKey, setTestApiKey] = useState('');
  const [testText, setTestText] = useState('Hello, this is a test of the ameboGPT API.');
  const [testLanguage, setTestLanguage] = useState('english');
  const [testSpeaker, setTestSpeaker] = useState('chimobi');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  // Use the new authenticated endpoint for user API keys
  const { data: keys, isLoading } = useQuery('api-keys', () => apiClient.getUserAPIKeys());

  const createMutation = useMutation(
    () => apiClient.createUserAPIKey(newKeyName || undefined),
    {
      onSuccess: (data) => {
        setCreatedKey(data.api_key);
        queryClient.invalidateQueries('api-keys');
        toast.success('API key created successfully!');
      },
      onError: (error: any) => {
        toast.error(error.message || 'Failed to create API key');
      },
    }
  );

  const revokeMutation = useMutation(
    (keyId: number) => apiClient.revokeUserAPIKey(keyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('api-keys');
        toast.success('API key revoked');
      },
      onError: (error: any) => {
        toast.error(error.message || 'Failed to revoke API key');
      },
    }
  );

  const handleCreate = () => {
    createMutation.mutate();
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard!');
  };

  const handleTestAPIKey = async () => {
    if (!testApiKey.trim()) {
      toast.error('Please enter an API key to test');
      return;
    }

    if (!testText.trim()) {
      toast.error('Please enter some text to test');
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': testApiKey.trim(),
        },
        body: JSON.stringify({
          text: testText.trim(),
          language: testLanguage,
          speaker_name: testSpeaker,
          temperature: 0.1,
          repetition_penalty: 1.1,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setTestResult({
          success: true,
          data,
          audioUrl: data.audio_url.startsWith('http')
            ? data.audio_url
            : `${API_BASE_URL}${data.audio_url}`,
        });
        toast.success('✅ API key test successful!');
        queryClient.invalidateQueries('api-keys'); // Refresh keys to update usage
      } else {
        setTestResult({
          success: false,
          error: data.detail || 'Unknown error',
          status: response.status,
        });
        toast.error(`❌ Test failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || 'Network error',
      });
      toast.error(`❌ Test failed: ${error.message || 'Network error'}`);
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-40">
        <div>
          <div className="title-one mb-2">
            <h2>API Keys</h2>
          </div>
          <p className="text-dark">Manage your API keys for accessing ameboGPT</p>
        </div>
        <div className="d-flex gap-3">
          <button className="btn-two" onClick={() => setShowTestModal(true)}>
            Test API Key
          </button>
          <button className="btn-one" onClick={() => setShowCreateModal(true)}>
            Create API Key
          </button>
        </div>
      </div>

      {/* Created Key Modal */}
      {createdKey && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-30">
              <div className="modal-header border-0 pb-0">
                <h2 className="modal-title">API Key Created</h2>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setCreatedKey(null);
                    setShowCreateModal(false);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-dark mb-4">
                  Store this key securely. You won't be able to see it again.
                </p>
                <div className="bg-light p-4 rounded border mb-4">
                  <code className="text-primary break-all">{createdKey}</code>
                </div>
                <div className="d-flex gap-3">
                  <button
                    className="btn-one flex-fill"
                    onClick={() => handleCopyKey(createdKey)}
                  >
                    Copy Key
                  </button>
                  <button
                    className="btn-two flex-fill"
                    onClick={() => {
                      setCreatedKey(null);
                      setShowCreateModal(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && !createdKey && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-30">
              <div className="modal-header border-0 pb-0">
                <h2 className="modal-title">Create API Key</h2>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-20">
                  <label className="d-block mb-2 text-dark">Name (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="My API Key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                  <small className="text-muted mt-2 d-block">
                    Give your API key a descriptive name to identify it later.
                  </small>
                </div>
                <div className="d-flex gap-3">
                  <button
                    className="btn-one flex-fill"
                    onClick={handleCreate}
                    disabled={createMutation.isLoading}
                  >
                    {createMutation.isLoading ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    className="btn-two flex-fill"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keys List */}
      {keys && keys.length > 0 ? (
        <div>
          {keys.map((key) => (
            <div key={key.id} className="card-style-one border-30 bg-white mb-20">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-fill">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <h4 className="title mb-0">{key.name}</h4>
                    <span className="badge bg-primary text-white">{key.plan}</span>
                    {!key.is_active && (
                      <span className="badge bg-danger text-white">Revoked</span>
                    )}
                  </div>
                  <p className="text-dark mb-4">
                    <code className="text-primary">{key.key_prefix}••••••••</code>
                  </p>
                  <div className="row gx-4">
                    <div className="col-6 col-md-3">
                      <p className="text-dark mb-1">Created</p>
                      <p className="text-dark fw-500">{formatDateTime(key.created_at)}</p>
                    </div>
                    <div className="col-6 col-md-3">
                      <p className="text-dark mb-1">Last Used</p>
                      <p className="text-dark fw-500">
                        {key.last_used_at ? formatDateTime(key.last_used_at) : 'Never'}
                      </p>
                    </div>
                    <div className="col-6 col-md-3">
                      <p className="text-dark mb-1">Requests</p>
                      <p className="text-dark fw-500">{key.total_requests.toLocaleString()}</p>
                    </div>
                    <div className="col-6 col-md-3">
                      <p className="text-dark mb-1">Characters</p>
                      <p className="text-dark fw-500">{key.total_characters.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                {key.is_active && (
                  <div className="d-flex flex-column gap-2 ms-3">
                    <button
                      className="btn-one"
                      onClick={() => {
                        toast('💡 Paste your full API key in the test modal. We only show the prefix for security.', {
                          duration: 4000,
                        });
                        setShowTestModal(true);
                      }}
                    >
                      Test
                    </button>
                    <button
                      className="btn-two"
                      onClick={() => revokeMutation.mutate(key.id)}
                    >
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-style-one border-30 bg-white text-center py-5">
          <p className="text-dark mb-4">No API keys yet</p>
          <button className="btn-one" onClick={() => setShowCreateModal(true)}>
            Create Your First API Key
          </button>
        </div>
      )}

      {/* Test API Key Modal */}
      {showTestModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-30">
              <div className="modal-header border-0 pb-0">
                <h2 className="modal-title">Test API Key</h2>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTestModal(false);
                    setTestResult(null);
                    setTestApiKey('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-20">
                  <label className="d-block mb-2 text-dark">API Key</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your API key"
                    value={testApiKey}
                    onChange={(e) => setTestApiKey(e.target.value)}
                  />
                  <small className="text-muted mt-2 d-block">
                    Paste your full API key here to test it
                  </small>
                </div>

                <div className="form-group mb-20">
                  <label className="d-block mb-2 text-dark">Test Text</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter text to convert to speech"
                    value={testText}
                    onChange={(e) => setTestText(e.target.value)}
                  />
                </div>

                <div className="row gx-3 mb-20">
                  <div className="col-6">
                    <label className="d-block mb-2 text-dark">Language</label>
                    <select
                      className="form-control"
                      value={testLanguage}
                      onChange={(e) => setTestLanguage(e.target.value)}
                    >
                      <option value="english">English</option>
                      <option value="hausa">Hausa</option>
                      <option value="igbo">Igbo</option>
                      <option value="yoruba">Yoruba</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="d-block mb-2 text-dark">Voice</label>
                    <select
                      className="form-control"
                      value={testSpeaker}
                      onChange={(e) => setTestSpeaker(e.target.value)}
                    >
                      <option value="chimobi">Chimobi (Female)</option>
                      <option value="tope">Tope (Female)</option>
                      <option value="larai">Larai (Female)</option>
                      <option value="uche">Uche (Male)</option>
                      <option value="ade">Ade (Male)</option>
                    </select>
                  </div>
                </div>

                {testResult && (
                  <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'} border-30 mb-20`}>
                    {testResult.success ? (
                      <div>
                        <h5 className="mb-2">✅ Test Successful!</h5>
                        <p className="mb-2">Request ID: <code>{testResult.data.request_id}</code></p>
                        {testResult.audioUrl && (
                          <div className="mt-3">
                            <p className="mb-2">Audio generated:</p>
                            <audio controls className="w-100">
                              <source src={testResult.audioUrl} type="audio/wav" />
                              Your browser does not support the audio element.
                            </audio>
                            <a
                              href={testResult.audioUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-two mt-2 d-inline-block"
                            >
                              Download Audio
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <h5 className="mb-2">❌ Test Failed</h5>
                        <p className="mb-0">
                          <strong>Error:</strong> {testResult.error}
                          {testResult.status && ` (Status: ${testResult.status})`}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="d-flex gap-3">
                  <button
                    className="btn-one flex-fill"
                    onClick={handleTestAPIKey}
                    disabled={isTesting || !testApiKey.trim() || !testText.trim()}
                  >
                    {isTesting ? 'Testing...' : 'Test API Key'}
                  </button>
                  <button
                    className="btn-two flex-fill"
                    onClick={() => {
                      setShowTestModal(false);
                      setTestResult(null);
                      setTestApiKey('');
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysPage;

